import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {
  ModuleSourceEnum,
  useRenderBalanceInvoiceDocumentMutation,
  useRenderCorrectiveTaxDocumentMutation,
  useRenderInvoiceDocumentMutation,
  useRenderInvoiceProformaDocumentMutation,
  useRenderTaxDocumentForPaymentDocumentMutation,
} from '@dms/api/finance';

import {Nullish} from 'shared/web';

import {InvoiceTypes} from '../types/InvoiceTypes';
import {ServiceCaseInvoiceType} from '../types/ServiceCaseInvoiceType';
import {downloadFile} from '../utils/downloadFile';
import {handleApiError} from '../utils/handleApiError';
import {printPDF} from '../utils/printPDF';
import {useDownloadServiceCaseInvoiceDocument} from './useDownloadServiceCaseInvoiceDocument';

export const useDownloadAccountingDocument = (shouldPrintTheDocument = false) => {
  const [
    getProformaDocument,
    {isLoading: loadingProformaDocument, isError: isProformaDocumentError},
  ] = useRenderInvoiceProformaDocumentMutation();
  const [getInvoiceDocument, {isLoading: loadingInvoiceDocument, isError: isInvoiceDocumentError}] =
    useRenderInvoiceDocumentMutation();
  const [
    getBalanceInvoiceDocument,
    {isLoading: loadingBalanceInvoiceDocument, isError: isBalanceInvoiceDocumentError},
  ] = useRenderBalanceInvoiceDocumentMutation();
  const [
    getCorrectiveTaxDocument,
    {isLoading: loadingCorrectiveTaxDocument, isError: isCorrectiveTaxDocumentError},
  ] = useRenderCorrectiveTaxDocumentMutation();
  const [
    getTaxDocumentForPayment,
    {isLoading: loadingTaxDocumentForPayment, isError: isTaxDocumentForPaymentError},
  ] = useRenderTaxDocumentForPaymentDocumentMutation();

  const [
    downloadServiceCaseInvoiceDocument,
    {
      isLoading: isDownloadingServiceCaseInvoiceDocument,
      isError: isErrorServiceCaseInvoiceDocument,
    },
  ] = useDownloadServiceCaseInvoiceDocument(shouldPrintTheDocument);

  const isLoading =
    loadingProformaDocument ||
    loadingInvoiceDocument ||
    loadingBalanceInvoiceDocument ||
    loadingCorrectiveTaxDocument ||
    loadingTaxDocumentForPayment;
  const isError =
    isProformaDocumentError ||
    isInvoiceDocumentError ||
    isBalanceInvoiceDocumentError ||
    isCorrectiveTaxDocumentError ||
    isTaxDocumentForPaymentError;

  const downloadInvoice = async (
    invoiceType: InvoiceTypes | Nullish,
    invoiceId: string,
    moduleSource: ModuleSourceEnum | Nullish,
    invoiceNumber: string | Nullish,
    invoiceCategory?: 'default' | 'internal' | null,
    isMargin = false
  ) => {
    if (isNil(invoiceType)) {
      showNotification.error();
      return;
    }

    const downloadAction = match(invoiceType)
      .with('PROFORMA', () => getProformaDocument)
      .with('TAX_DOCUMENT_FOR_PAYMENT', () => getTaxDocumentForPayment)
      .with('BALANCE_INVOICE', () => getBalanceInvoiceDocument)
      .with('CORRECTIVE_TAX_DOCUMENT', () => getCorrectiveTaxDocument)
      .otherwise(() => getInvoiceDocument);

    const renderDocumentRequestBody = {
      documentId: invoiceId,
      isMarginPrint: isMargin,
    };

    const serviceCaseType = match<InvoiceTypes, ServiceCaseInvoiceType>(invoiceType)
      .with('PROFORMA', () => 'invoice-proforma')
      .with('TAX_DOCUMENT_FOR_PAYMENT', () => 'tax-document-for-payment')
      .with('CORRECTIVE_TAX_DOCUMENT', () => 'corrective-tax-document')
      .with('BALANCE_INVOICE', () => 'balance-invoice')
      .with('ISSUED', () => (invoiceCategory === 'internal' ? 'invoice-internal' : 'invoice'))
      .otherwise(() => 'invoice');

    if (
      invoiceType !== 'DRAFT' &&
      ['service-case', 'service-direct'].includes(moduleSource as string)
    ) {
      downloadServiceCaseInvoiceDocument(invoiceId, invoiceNumber, serviceCaseType);
    } else {
      await downloadAction({
        renderDocumentRequestBody,
      })
        .unwrap()
        .then((res) => res.pdfUrl)
        .then(shouldPrintTheDocument ? printPDF : downloadFile)
        .catch(handleApiError);
    }
  };

  return [
    downloadInvoice,
    {
      isLoading: isLoading || isDownloadingServiceCaseInvoiceDocument,
      isError: isError || isErrorServiceCaseInvoiceDocument,
    },
  ] as const;
};
